import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Brain, 
  Activity, 
  Sparkles,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Beaker,
  Scale,
  Moon,
  Flame,
  Pill,
  Leaf,
  Shield,
  Clock,
  Users,
} from 'lucide-react';
import { getFooterText } from '@/config/version';

// ============================================================================
// STATS SECTION
// ============================================================================
const StatsSection: React.FC = () => (
  <section className="py-16 px-6" style={{ background: '#0a0f1a' }}>
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {[
          { value: '1 in 10', label: 'people with ovaries affected worldwide' },
          { value: '70%', label: 'remain undiagnosed' },
          { value: '4', label: 'distinct phenotypes' },
          { value: '50%', label: 'have insulin resistance' },
        ].map((stat, i) => (
          <div key={i} className="text-center">
            <div className="text-3xl md:text-4xl font-light mb-2" style={{ color: '#2FFFC7' }}>{stat.value}</div>
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================================================================
// PHENOTYPES SECTION
// ============================================================================
const PhenotypesSection: React.FC = () => {
  const phenotypes = [
    {
      letter: 'A',
      name: 'Classic PCOS',
      aka: 'Full Spectrum',
      color: '#FF6B6B',
      criteria: ['High androgens', 'Irregular/absent ovulation', 'Polycystic ovaries on ultrasound'],
      prevalence: '~60% of PCOS cases',
    },
    {
      letter: 'B',
      name: 'Non-PCO PCOS',
      aka: 'Classic without PCO',
      color: '#2FFFC7',
      criteria: ['High androgens', 'Irregular/absent ovulation', 'Normal-appearing ovaries'],
      prevalence: '~10% of PCOS cases',
    },
    {
      letter: 'C',
      name: 'Ovulatory PCOS',
      aka: 'Non-classic',
      color: '#45B7D1',
      criteria: ['High androgens', 'Polycystic ovaries', 'Regular cycles'],
      prevalence: '~15% of PCOS cases',
    },
    {
      letter: 'D',
      name: 'Non-Hyperandrogenic',
      aka: 'Mild PCOS',
      color: '#96CEB4',
      criteria: ['Normal androgens', 'Irregular/absent ovulation', 'Polycystic ovaries'],
      prevalence: '~15% of PCOS cases',
    },
  ];

  return (
    <section className="py-20 px-6" style={{ background: '#0a0f1a' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#2FFFC7' }}>The Four Types</p>
          <h2 className="text-3xl md:text-4xl font-light mb-4" style={{ color: '#E6E9F0' }}>
            PCOS <span className="italic" style={{ color: '#2FFFC7' }}>Phenotypes</span>
          </h2>
          <p className="max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Not all PCOS is the same. Based on the Rotterdam criteria, there are four distinct phenotypes.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {phenotypes.map((phenotype) => (
            <div
              key={phenotype.letter}
              className="p-6 rounded-2xl transition-all"
              style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold"
                  style={{ backgroundColor: phenotype.color, color: '#000' }}
                >
                  {phenotype.letter}
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>{phenotype.name}</h3>
                  <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>{phenotype.aka}</p>
                </div>
                <span className="ml-auto text-xs" style={{ color: 'rgba(255, 255, 255, 0.3)' }}>{phenotype.prevalence}</span>
              </div>
              
              <ul className="space-y-1">
                {phenotype.criteria.map((criterion, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    <CheckCircle className="w-3.5 h-3.5" style={{ color: '#2FFFC7' }} />
                    {criterion}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// ENDOTYPES SECTION
// ============================================================================
const EndotypesSection: React.FC = () => {
  const endotypes = [
    {
      icon: Scale,
      name: 'Insulin-Resistant',
      color: '#FF9F43',
      signs: ['Weight around the middle', 'Sugar cravings', 'Fatigue after meals', 'High fasting insulin'],
      approach: 'Focus on blood sugar balance through diet, exercise, and potentially metformin or GLP-1 agonists.',
    },
    {
      icon: Pill,
      name: 'Post-Pill',
      color: '#A55EEA',
      signs: ['Symptoms started after stopping birth control', 'Previously regular periods', 'Often temporary (6-12 months)'],
      approach: 'Support natural hormone recovery with patience, nutrition, and possibly herbs.',
    },
    {
      icon: Flame,
      name: 'Adrenal',
      color: '#FC5C65',
      signs: ['Elevated DHEA-S (not testosterone)', 'High stress levels', 'Poor sleep', 'Often normal weight'],
      approach: 'Prioritize stress reduction, sleep, adaptogens, and adrenal support.',
    },
    {
      icon: Shield,
      name: 'Inflammatory',
      color: '#26DE81',
      signs: ['Gut issues (IBS, bloating)', 'Food sensitivities', 'Joint pain', 'Elevated CRP'],
      approach: 'Address gut health, remove inflammatory triggers, support with anti-inflammatory foods.',
    },
  ];

  return (
    <section className="py-20 px-6" style={{ background: 'linear-gradient(180deg, #0a0f1a 0%, rgba(47, 255, 199, 0.05) 50%, #0a0f1a 100%)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#2FFFC7' }}>Root Causes</p>
          <h2 className="text-3xl md:text-4xl font-light mb-4" style={{ color: '#E6E9F0' }}>
            The Four <span className="italic" style={{ color: '#2FFFC7' }}>Endotypes</span>
          </h2>
          <p className="max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Beyond phenotypes, understanding the underlying driver of your PCOS helps target treatment at the root cause.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {endotypes.map((endotype) => {
            const Icon = endotype.icon;
            return (
              <div
                key={endotype.name}
                className="p-6 rounded-2xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${endotype.color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: endotype.color }} />
                  </div>
                  <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>{endotype.name} PCOS</h3>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Common Signs</p>
                  <ul className="space-y-1">
                    {endotype.signs.map((sign, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: endotype.color }} />
                        {sign}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Treatment Focus</p>
                  <p className="text-sm" style={{ color: '#2FFFC7' }}>{endotype.approach}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// MAIN LANDING PAGE
// ============================================================================
const PCOSLanding: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a0f1a 0%, #131a2b 100%)' }}>
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Background gradient */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(47, 255, 199, 0.1) 0%, transparent 50%, rgba(59, 130, 246, 0.1) 100%)',
          }}
        />

        <div className="relative z-10 min-h-screen flex flex-col justify-between px-6 md:px-12 lg:px-20 py-8">
          {/* Top badge */}
          <div className="pt-20">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Free Assessment • 10 Minutes
              </span>
            </div>
          </div>

          {/* Main content */}
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light leading-tight mb-6" style={{ color: '#E6E9F0' }}>
              Could You
              <br />
              Have <span className="italic" style={{ color: '#2FFFC7' }}>PCOS</span>?
            </h1>

            <div className="flex items-start gap-6 mt-8">
              <button
                onClick={() => navigate('/pcos/quiz')}
                className="flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center hover:scale-105 transition-transform"
                style={{
                  background: '#2FFFC7',
                  boxShadow: '0 8px 32px rgba(47, 255, 199, 0.3)',
                }}
              >
                <span className="text-black font-semibold text-sm">Start</span>
              </button>
              <div className="pt-2">
                <p className="text-lg leading-relaxed max-w-md" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Find out which type of PCOS you may have. 
                  Get clear answers and next steps tailored to you.
                </p>
              </div>
            </div>
          </div>

          <div className="pb-8" />
        </div>
      </section>

      <StatsSection />
      
      {/* What is PCOS Section */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(180deg, #0a0f1a 0%, rgba(47, 255, 199, 0.03) 100%)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#2FFFC7' }}>Understanding the Basics</p>
            <h2 className="text-3xl md:text-4xl font-light mb-4" style={{ color: '#E6E9F0' }}>
              What is <span className="italic" style={{ color: '#2FFFC7' }}>PCOS</span>?
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div 
              className="p-8 rounded-2xl"
              style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#E6E9F0' }}>More Than Just Cysts</h3>
              <p className="mb-6 leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Despite its name, PCOS isn't primarily about cysts on the ovaries. It's a 
                <strong style={{ color: '#E6E9F0' }}> complex hormonal and metabolic syndrome</strong> involving:
              </p>
              <ul className="space-y-3">
                {[
                  'Hormonal imbalance (often elevated androgens)',
                  'Irregular or absent ovulation',
                  'Metabolic dysfunction (insulin resistance)',
                  'Systemic effects on mental health, sleep, and cardiovascular risk',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div 
              className="p-8 rounded-2xl"
              style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#E6E9F0' }}>Common Symptoms</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Activity, label: 'Irregular periods' },
                  { icon: Sparkles, label: 'Acne' },
                  { icon: Sparkles, label: 'Excess hair growth' },
                  { icon: Brain, label: 'Hair thinning' },
                  { icon: Scale, label: 'Weight changes' },
                  { icon: Heart, label: 'Fertility issues' },
                  { icon: Moon, label: 'Sleep problems' },
                  { icon: AlertTriangle, label: 'Mood changes' },
                ].map((symptom, i) => {
                  const Icon = symptom.icon;
                  return (
                    <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(0, 0, 0, 0.3)' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4" style={{ color: '#2FFFC7' }} />
                        <span className="text-sm font-medium" style={{ color: '#E6E9F0' }}>{symptom.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <PhenotypesSection />
      <EndotypesSection />
      
      {/* CTA Section */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(180deg, #0a0f1a 0%, #000 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div 
            className="p-12 rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, rgba(47, 255, 199, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
              style={{ background: '#2FFFC7' }}
            >
              <Users className="w-8 h-8 text-black" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-light mb-4" style={{ color: '#E6E9F0' }}>
              Ready to Understand <span className="italic" style={{ color: '#2FFFC7' }}>Your</span> PCOS?
            </h2>
            
            <p className="mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Take our comprehensive 10-minute assessment to identify your likely phenotype and endotype. 
              Get personalized insights and recommendations based on the latest evidence.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <button
                onClick={() => navigate('/pcos/quiz')}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105"
                style={{
                  background: '#2FFFC7',
                  color: '#000',
                  boxShadow: '0 8px 32px rgba(47, 255, 199, 0.3)',
                }}
              >
                Take the PCOS Quiz
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center justify-center gap-6 text-sm" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" style={{ color: '#2FFFC7' }} />
                Free to take
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" style={{ color: '#2FFFC7' }} />
                ~10 minutes
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" style={{ color: '#2FFFC7' }} />
                Evidence-based
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 px-6" style={{ background: '#000', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-6xl mx-auto">
          <p className="text-xs text-center leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            <strong style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Medical Disclaimer:</strong> This assessment is for educational purposes only and does not constitute medical advice, diagnosis, or treatment. 
            Always consult with a qualified healthcare professional for proper diagnosis and treatment of PCOS.
          </p>
          <p className="text-xs text-center mt-4" style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            {getFooterText()}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PCOSLanding;
